import { useEffect, useRef, useContext, useState } from 'react';
import { io } from 'socket.io-client';
import { AuthContext } from '../context/AuthContext';
import { ErrorContext } from '../context/ErrorContext';

const useSocket = (url, nationId, planId, handlers = {}) => {
    const {
        onTileUpdate,
        onTileError,
        onOptimizeEvent,
        onDrawingUpdate,
        onDrawingError,
        onBorderUpdate,
        onBorderError
    } = handlers;
    const socket = useRef(null);
    const handlersRef = useRef({
        onTileUpdate,
        onTileError,
        onOptimizeEvent,
        onDrawingUpdate,
        onDrawingError,
        onBorderUpdate,
        onBorderError
    });
    const [socketId, setSocketId] = useState(null);
    const {
        isAuthenticated,
        markSessionExpired,
        getAccessToken,
        refreshAccessToken,
        tokenVersion,
    } = useContext(AuthContext);
    const { showError } = useContext(ErrorContext);

    useEffect(() => {
        handlersRef.current.onTileUpdate = onTileUpdate;
    }, [onTileUpdate]);

    useEffect(() => {
        handlersRef.current.onTileError = onTileError;
    }, [onTileError]);

    useEffect(() => {
        handlersRef.current.onOptimizeEvent = onOptimizeEvent;
    }, [onOptimizeEvent]);

    useEffect(() => {
        handlersRef.current.onDrawingUpdate = onDrawingUpdate;
    }, [onDrawingUpdate]);

    useEffect(() => {
        handlersRef.current.onDrawingError = onDrawingError;
    }, [onDrawingError]);

    useEffect(() => {
        handlersRef.current.onBorderUpdate = onBorderUpdate;
    }, [onBorderUpdate]);

    useEffect(() => {
        handlersRef.current.onBorderError = onBorderError;
    }, [onBorderError]);

    const isExplicitAuthError = (message) => {
        const normalized = String(message || '').toLowerCase().trim();
        return (
            normalized === 'authentication required' ||
            normalized === 'please authenticate' ||
            normalized.includes('jwt expired') ||
            normalized.includes('invalid token') ||
            normalized.includes('token expired')
        );
    };

    useEffect(() => {
        if (!nationId || !planId) return undefined;

        let cancelled = false;
        let retryAttempted = false;

        const attachSocketListeners = (activeSocket) => {
            activeSocket.on('connect', () => {
                setSocketId(activeSocket.id || null);
                retryAttempted = false;
            });

            activeSocket.on('disconnect', () => {
                setSocketId(null);
            });

            activeSocket.emit('joinRoom', { nationId, planId });

            activeSocket.on('newTile', (tileData) => {
                const handler = handlersRef.current.onTileUpdate;
                if (handler) handler({ action: 'newTile', tileData });
            });

            activeSocket.on('deletedTile', (tileData) => {
                const handler = handlersRef.current.onTileUpdate;
                if (handler) handler({ action: 'deletedTile', tileData });
            });

            activeSocket.on('tileActionError', (payload) => {
                const handler = handlersRef.current.onTileError;
                if (handler) handler(payload);
            });

            activeSocket.on('optimizeTerritoryEvent', (payload) => {
                const handler = handlersRef.current.onOptimizeEvent;
                if (handler) handler(payload);
            });

            activeSocket.on('drawingUpserted', (drawing) => {
                const handler = handlersRef.current.onDrawingUpdate;
                if (handler) handler({ action: 'drawingUpserted', drawing });
            });

            activeSocket.on('drawingDeleted', ({ drawingId }) => {
                const handler = handlersRef.current.onDrawingUpdate;
                if (handler) handler({ action: 'drawingDeleted', drawingId });
            });

            activeSocket.on('drawingActionError', (payload) => {
                const drawingErrorHandler = handlersRef.current.onDrawingError;
                if (drawingErrorHandler) {
                    drawingErrorHandler(payload);
                    return;
                }
                const tileErrorHandler = handlersRef.current.onTileError;
                if (tileErrorHandler) tileErrorHandler(payload);
            });

            activeSocket.on('borderUpserted', (border) => {
                const handler = handlersRef.current.onBorderUpdate;
                if (handler) handler({ action: 'borderUpserted', border });
            });

            activeSocket.on('borderDeleted', ({ borderId, allianceId }) => {
                const handler = handlersRef.current.onBorderUpdate;
                if (handler) handler({ action: 'borderDeleted', borderId, allianceId });
            });

            activeSocket.on('borderActionError', (payload) => {
                const borderErrorHandler = handlersRef.current.onBorderError;
                if (borderErrorHandler) {
                    borderErrorHandler(payload);
                    return;
                }
                const tileErrorHandler = handlersRef.current.onTileError;
                if (tileErrorHandler) tileErrorHandler(payload);
            });

            activeSocket.on('sessionExpired', async () => {
                if (retryAttempted || cancelled) return;
                retryAttempted = true;
                try {
                    await refreshAccessToken({ force: true });
                    const freshToken = getAccessToken();
                    if (!freshToken || cancelled) throw new Error('No access token after refresh');
                    activeSocket.auth = { token: `Bearer ${freshToken}` };
                    activeSocket.connect();
                } catch (error) {
                    markSessionExpired();
                    activeSocket.disconnect();
                }
            });

            const handleConnectError = async (error) => {
                const message = error?.message || error?.data?.message || '';
                const isAuthError = isExplicitAuthError(message);

                if (isAuthError) {
                    if (retryAttempted || cancelled) {
                        markSessionExpired();
                        activeSocket.disconnect();
                        return;
                    }
                    retryAttempted = true;
                    try {
                        await refreshAccessToken({ force: true });
                        const freshToken = getAccessToken();
                        if (!freshToken) {
                            throw new Error('Unable to refresh token for socket');
                        }
                        activeSocket.auth = { token: `Bearer ${freshToken}` };
                        activeSocket.connect();
                        return;
                    } catch (refreshError) {
                        markSessionExpired();
                        activeSocket.disconnect();
                        return;
                    }
                }

                if (message) showError(message);
                else showError('Socket connection error. Please try again.');
            };

            const handleSocketError = (response) => {
                const message = response?.message || response;
                const isAuthError = isExplicitAuthError(message);

                if (isAuthError) {
                    markSessionExpired();
                    activeSocket.disconnect();
                    return;
                }

                const handler = handlersRef.current.onTileError;
                if (handler) handler({ message });
                else if (message) showError(message);
            };

            activeSocket.on('error', handleSocketError);
            activeSocket.on('connect_error', handleConnectError);
        };

        const connectSocket = async () => {
            let token = getAccessToken();
            if (!token && isAuthenticated()) {
                try {
                    await refreshAccessToken({ force: true });
                    token = getAccessToken();
                } catch (error) {
                    markSessionExpired();
                    return;
                }
            }

            if (!token || cancelled) return;

            const activeSocket = io(url, {
                auth: { token: `Bearer ${token}` },
                transports: ['websocket'],
            });
            socket.current = activeSocket;
            attachSocketListeners(activeSocket);
        };

        connectSocket();

        return () => {
            cancelled = true;
            const activeSocket = socket.current;
            if (!activeSocket) return;
            activeSocket.off('newTile');
            activeSocket.off('deletedTile');
            activeSocket.off('tileActionError');
            activeSocket.off('optimizeTerritoryEvent');
            activeSocket.off('drawingUpserted');
            activeSocket.off('drawingDeleted');
            activeSocket.off('drawingActionError');
            activeSocket.off('borderUpserted');
            activeSocket.off('borderDeleted');
            activeSocket.off('borderActionError');
            activeSocket.off('error');
            activeSocket.off('connect_error');
            activeSocket.off('sessionExpired');
            activeSocket.off('connect');
            activeSocket.off('disconnect');
            activeSocket.disconnect();
            socket.current = null;
            setSocketId(null);
        };
    }, [
        url,
        nationId,
        planId,
        getAccessToken,
        isAuthenticated,
        markSessionExpired,
        refreshAccessToken,
        tokenVersion,
        showError,
    ]);

    useEffect(() => {
        if ((!nationId || !planId) && socket.current) {
            socket.current.disconnect();
            socket.current = null;
            setSocketId(null);
        }
    }, [nationId, planId]);

    const emitTile = (tileUpdate) => {
        if (socket.current) {
            socket.current.emit(tileUpdate.action, tileUpdate.tileData);
        }
    };

    return { emitTile, socketId };
};

export default useSocket;
